<script setup lang="ts">
import type { BeltColor } from '@@/bits/belt_colors'
import { getUntranslatedCertifiedBeltText } from '@@/bits/belt_colors'
import { fitText } from '@@/bits/fit_text'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    name: string
    beltColor: Exclude<BeltColor, 'white'>
  }>(),
  {
    name: '',
    beltColor: 'yellow',
  },
)

const beltTitle = computed(() => {
  return getUntranslatedCertifiedBeltText(props.beltColor)
})

const nameTitleRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
let cleanupFitText: (() => void) | null = null
let resizeObserver: ResizeObserver | null = null
const xIsFitTextScheduled = ref(false)

const applyFitText = () => {
  if (xIsFitTextScheduled.value) return
  xIsFitTextScheduled.value = true

  requestAnimationFrame(async () => {
    xIsFitTextScheduled.value = false
    if (!nameTitleRef.value) return

    cleanupFitText?.()
    await nextTick()

    if (!nameTitleRef.value) return
    cleanupFitText = fitText(nameTitleRef.value, {
      minSize: 20,
      maxSize: 60,
      maxLines: 2,
    })
  })
}

onMounted(() => {
  applyFitText()
  if (containerRef.value) {
    resizeObserver = new ResizeObserver(applyFitText)
    resizeObserver.observe(containerRef.value)
  }
})

watch(() => props.name, applyFitText)

onUnmounted(() => {
  cleanupFitText?.()
  resizeObserver?.disconnect()
})
</script>

<template>
  <div
    ref="containerRef"
    class="flex flex-col items-center justify-center gap-y-1.5"
    style="max-width: 650px; width: 100%"
  >
    <div
      ref="nameTitleRef"
      :class="['name-title', beltColor === 'black' ? 'text-light-text-100' : 'text-dark-text-100']"
    >
      {{ name }}
    </div>
    <div :class="['belt-title', beltColor === 'black' ? 'text-[#F4F4F4]' : 'text-[#222222]']">
      {{ beltTitle }}
    </div>
  </div>
</template>

<style scoped>
@import '@@/styles/caveat_brush.scss';
@import '@@/styles/eb_garamond.scss';
@import '@@/styles/3/partials/fonts/_shantell.scss';
.name-title {
  font-family: 'Caveat Brush', 'Shantell Sans', 'Comic Sans MS', cursive;
  font-size: 60px;
  font-weight: 400;
  line-height: 42px;
  text-align: center;
}

.belt-title {
  font-family: 'EB Garamond';
  font-size: 24px;
  font-weight: 700; /* goodcheck-disable-line */
  line-height: 26px;
  text-transform: uppercase;
  text-align: center;
}
</style>
